// src/context/AuthContext.js
// ──────────────────────────────────────────────────────────────────────────
// Wraps Supabase Auth: exposes the current user, loading state, and
// sign in / sign up / sign out. Use via the useAuth() hook.
// ──────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, supabaseReady } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState(null); // 'owner' | 'content' | null
  const [role, setRole] = useState('student');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabaseReady) { setLoading(false); return undefined; }
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Admin status (admins table) + role (profiles table)
  useEffect(() => {
    if (!supabaseReady || !user) { setIsAdmin(false); setAdminRole(null); setRole('student'); return; }
    if (user.email) {
      supabase.from('admins').select('email, role').eq('email', user.email).maybeSingle()
        .then(({ data }) => {
          setIsAdmin(Boolean(data));
          // Rows created before admin tiers existed have no role — treat them
          // as owners, which is what they were.
          setAdminRole(data ? (data.role || 'owner') : null);
        });
    }
    supabase.from('profiles').select('role').eq('id', user.id).maybeSingle()
      .then(({ data }) => setRole(data?.role || 'student'));
  }, [user]);

  const isStaff = isAdmin || role === 'staff' || role === 'admin';
  // Owners manage accounts and the admin list; content managers do not. The
  // UI hides those areas, but every one is enforced by RLS as well.
  const isOwner = adminRole === 'owner';

  const signIn  = (email, password) => supabase.auth.signInWithPassword({ email, password });
  const signUp  = (email, password) => supabase.auth.signUp({ email, password });
  const signOut = () => supabase.auth.signOut();

  return (
    <AuthContext.Provider value={{ user, isAdmin, isOwner, adminRole, isStaff, role, loading, ready: supabaseReady, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
